import { Reducer } from 'redux'

import { LOGIN_ACTION, LoginAction } from 'actions/authorization/loginAction'
import { LOGOUT_ACTION, LogoutAction } from 'actions/authorization/logoutAction'
import {
  DISLIKE_PHOTO_NEXT,
  dislikePhotoPlainAction,
} from 'actions/rating/dislikePhotoNextAction'
import {
  CLEAR_ERROR,
  RATING_ERROR,
  ratingClearErrorAction,
  ratingErrorAction,
} from 'actions/rating/errorAction'
import {
  LIKE_PHOTO_NEXT,
  likePhotoNextPlainAction,
} from 'actions/rating/likePhotoNextAction'
import {
  CLEAR_PHOTOS_QUEUE,
  clearPhotosQueueAction,
  CREATE_PHOTOS_QUEUE,
  createPhotosQueueAction,
} from 'actions/rating/photosNextAction'
import {
  INCOGNITO_SAVE_POSITION,
  incognitoSavePositionAction,
  PROMO_INCREASE_VOTED_COUNTER,
  promoIncreaseVotedCounter,
  SAVE_PROMO,
  savePromoAction,
} from 'actions/rating/promoNextAction'
import {
  RESET_PHOTO_VOTE,
  resetPhotoVoteAction,
} from 'actions/rating/resetPhotoVoteAction'
import { ROLL_BACK, rollbackAction } from 'actions/rating/rollbackAction'
import {
  RATING_FETCH_MORE,
  setRatingFetchMoreAction,
} from 'actions/rating/setRatingFetchMoreAction'
import {
  RESTORE_PARTIAL_STATE,
  restorePartialStateAction,
} from 'actions/system/localStorageAction'
import { RatingPromo } from 'components/page/PhotoRating/PhotoRating.constants'
import { GQLRatingQuery } from 'components/page/RatingWave/gql/__generated__/Rating'
import { LIMIT_PHOTOS } from 'components/page/RatingWave/Rating.constants'
import { defaultPromiseReducer } from 'reducers/defaultPromiseReducer'

type Photos = GQLRatingQuery['photoRating']['queue']['photos']

export interface PhotoRatingNextState {
  loading: boolean
  votedCounter: number
  likedCounter: number
  rollbackCounter: number
  rollbackLoading: boolean
  incognitoPositionShowed: number
  promo: RatingPromo | null
  promoIndex: number
  currentQueuePhotos: Photos
  votedPhotos: Photos
  hasNextPage: boolean
  votedPhotosCounter: number
  error: string | null
}

const initialState = {
  loading: false,
  votedCounter: 0,
  likedCounter: 0,
  rollbackCounter: -1,
  rollbackLoading: false,
  incognitoPositionShowed: 0,
  promo: null,
  promoIndex: 0,
  currentQueuePhotos: [],
  votedPhotos: [],
  /**
   * Данный флаг равен votedCounter когда шагаем вперед
   * и каждый раз отнимается когда шагаем назад
   * Доходим до 0 и с помощью этого флага прячем стрелку назад.
   */
  votedPhotosCounter: 0,
  error: null,
  hasNextPage: true,
}

const VOTED_PHOTOS_LIMIT_STORAGE = 50

export const photoRatingNextReducer: Reducer<
  PhotoRatingNextState,
  | ReturnType<typeof createPhotosQueueAction>
  | ReturnType<typeof likePhotoNextPlainAction>
  | ReturnType<typeof dislikePhotoPlainAction>
  | ReturnType<typeof rollbackAction>
  | ReturnType<typeof restorePartialStateAction>
  | ReturnType<typeof savePromoAction>
  | ReturnType<typeof incognitoSavePositionAction>
  | ReturnType<typeof promoIncreaseVotedCounter>
  | ReturnType<typeof clearPhotosQueueAction>
  | ReturnType<typeof clearPhotosQueueAction>
  | ReturnType<typeof ratingErrorAction>
  | ReturnType<typeof ratingClearErrorAction>
  | ReturnType<typeof setRatingFetchMoreAction>
  | ReturnType<typeof resetPhotoVoteAction>
  | LoginAction
  | LogoutAction
> = (state = initialState, action) => {
  switch (action.type) {
    case CREATE_PHOTOS_QUEUE:
      /** Даная операция должна выполнятся только один раз при загрузке, и только в том случая если vip пользователь откатился на несколько шагов назад и перезагрузил страницу. */
      if (state.rollbackCounter >= 0) {
        return {
          ...state,
          currentQueuePhotos: mergePhotos(
            [
              ...state.votedPhotos
                .slice(0, state.rollbackCounter + 1)
                .reverse()
                .map((photo) => ({
                  ...photo,
                  photo: {
                    ...photo.photo,
                    voted: false,
                    liked: false,
                  },
                })),
              ...state.currentQueuePhotos,
            ],
            action.photos
          ),
          loading: false,
          hasNextPage: action.photos.length === LIMIT_PHOTOS,
        }
      }

      return {
        ...state,
        currentQueuePhotos: mergePhotos(
          state.currentQueuePhotos,
          action.photos
        ),
        loading: false,
        hasNextPage: action.photos.length === LIMIT_PHOTOS,
      }

    case LIKE_PHOTO_NEXT:
      return defaultPromiseReducer(
        state,
        action,
        (_, state) => {
          /**
           * Для капчи,
           * Мы уходим на другую страницу, поэтому отчищаем данные.
           * И при возвращение запрашиваем новые данные.
           */
          if (state.currentQueuePhotos.length === 0) {
            return state
          }

          const [head, ...tail] = state.currentQueuePhotos

          // не красиво
          const photo = {
            ...head,
            photo: {
              ...head.photo,
              voted: true,
              liked: true,
            },
          }

          if (state.rollbackCounter >= 0) {
            /**
             * Мы храним фото в обоих массивах в случае если пользователь вернулся назад и решил перезагрузить.
             * после перезагрузки старые останутся в votedPhotos и можно будет восстановить
             */
            const photos = [...state.votedPhotos]
            photos[state.rollbackCounter] = photo

            return {
              rollbackCounter: state.rollbackCounter - 1,
              currentQueuePhotos: [...tail],
              votedPhotos: [...photos],
              votedPhotosCounter: state.votedPhotosCounter + 1,
            }
          }

          return {
            currentQueuePhotos: [...tail],
            votedPhotos: [photo, ...state.votedPhotos].slice(
              0,
              VOTED_PHOTOS_LIMIT_STORAGE
            ),
            votedCounter: state.votedCounter + 1,
            likedCounter: state.likedCounter + 1,
            votedPhotosCounter:
              state.votedPhotosCounter + 1 > VOTED_PHOTOS_LIMIT_STORAGE
                ? VOTED_PHOTOS_LIMIT_STORAGE
                : state.votedPhotosCounter + 1,
          }
        },
        // TODO: почему то срабатывает before, если убрать данную строку
        (_result, state) => ({ ...state }),
        (_result, _state, error) => {
          return {
            error: error?.code,
          }
        }
      )

    case DISLIKE_PHOTO_NEXT:
      return defaultPromiseReducer(
        state,
        action,
        (_result, state) => {
          /**
           * Для капчи,
           * Мы уходим на другую страницу, поэтому отчищаем данные.
           * И при возвращение запрашиваем новые данные.
           */
          if (state.currentQueuePhotos.length === 0) {
            return state
          }

          const [head, ...tail] = state.currentQueuePhotos

          // не красиво
          const photo = {
            ...head,
            photo: {
              ...head.photo,
              voted: true,
              liked: false,
            },
          }

          if (state.rollbackCounter >= 0) {
            /**
             * Мы храним фото в обоих массивах в случае если пользователь вернулся назад и решил перезагрузить.
             * после перезагрузки старые останутся в votedPhotos и можно будет восстановить
             */
            const photos = [...state.votedPhotos]
            photos[state.rollbackCounter] = photo

            return {
              rollbackCounter: state.rollbackCounter - 1,
              currentQueuePhotos: [...tail],
              votedPhotos: [...photos],
              votedPhotosCounter: state.votedPhotosCounter + 1,
            }
          }

          return {
            votedCounter: state.votedCounter + 1,
            currentQueuePhotos: [...tail],
            votedPhotos: [photo, ...state.votedPhotos].slice(
              0,
              VOTED_PHOTOS_LIMIT_STORAGE
            ),
            votedPhotosCounter:
              state.votedPhotosCounter + 1 > VOTED_PHOTOS_LIMIT_STORAGE
                ? VOTED_PHOTOS_LIMIT_STORAGE
                : state.votedPhotosCounter + 1,
          }
        },
        (_result, state) => ({ ...state }),
        (_result, _state, error) => {
          return {
            error: error?.code,
          }
        }
      )

    case ROLL_BACK:
      const counter = state.rollbackCounter + 1

      const photo = state.votedPhotos[counter]

      if (photo) {
        return {
          ...state,
          rollbackCounter: counter,
          votedPhotosCounter: state.votedPhotosCounter - 1 || 0,
          currentQueuePhotos: [photo, ...state.currentQueuePhotos],
          rollbackLoading: true,
        }
      }

      return state

    case SAVE_PROMO:
      return {
        ...state,
        promoIndex: action.promoIndex,
        promo: action.promoType,
      }

    case INCOGNITO_SAVE_POSITION:
      return {
        ...state,
        incognitoPositionShowed: state.likedCounter,
      }

    case PROMO_INCREASE_VOTED_COUNTER: {
      return {
        ...state,
        votedCounter: state.votedCounter + 1,
      }
    }

    case CLEAR_PHOTOS_QUEUE:
      return {
        ...state,
        loading: true,
        currentQueuePhotos: [],
        error: null,
      }

    case RATING_FETCH_MORE:
      return {
        ...state,
        loading: true,
      }

    case RESET_PHOTO_VOTE:
      const [head, ...tail] = state.currentQueuePhotos

      const _photo = {
        ...head,
        photo: {
          ...head.photo,
          voted: false,
          liked: false,
        },
      }

      return {
        ...state,
        rollbackLoading: false,
        currentQueuePhotos: [_photo, ...tail],
      }

    case LOGOUT_ACTION:
    case LOGIN_ACTION:
      return { ...initialState }

    case RESTORE_PARTIAL_STATE:
      if (action.name === 'photoRatingNext') {
        return {
          ...state,
          ...action.partialState?.photoRatingNext,
        }
      }

      return state

    case RATING_ERROR:
      return {
        ...state,
        error: action.value,
      }

    case CLEAR_ERROR:
      return {
        ...state,
        error: null,
      }

    default:
      return state
  }
}

const mergePhotos = (exciting: Photos, incoming: Photos) => {
  const idsArray = new Set(exciting.map((item) => item.photoOwner.id))

  const filteredArray = incoming.filter(
    (item) => !idsArray.has(item.photoOwner.id)
  )

  return [...exciting, ...filteredArray]
}
